/**
 * @fileoverview Create Prospect mutation hook.
 * @description Wraps POST /sales/prospects in a TanStack mutation and refreshes every Sales CRM
 * query once it succeeds. The caller passes the idempotency key, so retrying the same submission
 * after a network failure reuses it rather than creating a second prospect.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { SALES_QUERY_KEY } from '@/features/sales/constants/prospects-query-keys.constants'
import { createProspect } from '@/features/sales/services/prospects.service'

import type { CreateProspectRequest } from '@/features/sales/types/prospect.types'

/**
 * @description Creates a prospect and refreshes the pipeline.
 * @returns {ReturnType<typeof useMutation>} The mutation (mutate with the request and its key).
 */
export function useCreateProspect() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      request,
      idempotencyKey,
    }: {
      request: CreateProspectRequest
      idempotencyKey: string
    }) => createProspect(request, idempotencyKey),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SALES_QUERY_KEY })
    },
  })
}
